import type {
  CreativeDecisionFilterFacet,
  CreativeDecisionFilterFacets,
  CreativeDecisionMaturityState,
  CreativeDecisionPortfolioRequest,
  CreativeDecisionProjection,
  MoneyAmountBinding,
} from './contracts';

export interface CreativeDecisionFilterValues {
  evidenceClass: string[];
  campaignFamily: string[];
  network: string[];
  funnel: string[];
  geo: string[];
  product: string[];
  plan: string[];
  price: string[];
  originalCurrency: string[];
  reportingCurrency: string[];
  maturity: CreativeDecisionMaturityState[];
}

/**
 * Extracts only immutable, explicitly projected filter dimensions. Mutable
 * labels, URLs and provider resource names are deliberately not parsed to
 * manufacture funnel, geo or monetization identity.
 */
export function deriveCreativeDecisionFilterValues(
  row: CreativeDecisionProjection,
): CreativeDecisionFilterValues {
  const contexts = row.monetizationContexts?.length
    ? row.monetizationContexts
    : [row.monetization];
  const money = moneyBindings(row);
  const taxonomyFunnels = row.artifactSnapshots.flatMap((artifact) => (
    artifact.taxonomy?.values
      .filter((value) => value.dimension === 'funnel')
      .map((value) => value.valueKey) ?? []
  ));

  return {
    evidenceClass: clean(row.evidence.map((binding) => binding.evidenceClass)),
    campaignFamily: clean([row.capability.campaignFamily]),
    // Phase 1 uses the canonical paid-ads provider as the network identity.
    network: clean([row.deployment.provider]),
    funnel: clean([...(row.filterContext?.funnels ?? []), ...taxonomyFunnels]),
    geo: clean(row.filterContext?.geos ?? []),
    product: clean(contexts.map((context) => context.productId)),
    plan: clean([
      ...contexts.map((context) => context.planId),
      ...(row.filterContext?.planIds ?? []),
    ]),
    price: clean(contexts.map((context) => context.priceId)),
    originalCurrency: clean([
      ...money.map((binding) => binding.originalCurrency),
      ...(row.money.firstPartyOriginalCurrencyBreakdown ?? [])
        .map((bucket) => bucket.originalCurrency),
    ]).map((currency) => currency.toUpperCase()),
    reportingCurrency: clean(money.map((binding) => binding.reportingCurrency))
      .map((currency) => currency.toUpperCase()),
    maturity: [deriveMaturityState(row)],
  };
}

/** Facets are built from the unfiltered portfolio scope. */
export function buildCreativeDecisionFilterFacets(
  rows: readonly CreativeDecisionProjection[],
): CreativeDecisionFilterFacets {
  const values = rows.map(deriveCreativeDecisionFilterValues);
  return {
    evidenceClass: buildFacet(values.map((item) => item.evidenceClass), rows.length),
    campaignFamily: buildFacet(values.map((item) => item.campaignFamily), rows.length),
    network: buildFacet(values.map((item) => item.network), rows.length),
    funnel: buildFacet(values.map((item) => item.funnel), rows.length),
    geo: buildFacet(values.map((item) => item.geo), rows.length),
    product: buildFacet(values.map((item) => item.product), rows.length),
    plan: buildFacet(values.map((item) => item.plan), rows.length),
    price: buildFacet(values.map((item) => item.price), rows.length),
    originalCurrency: buildFacet(values.map((item) => item.originalCurrency), rows.length),
    reportingCurrency: buildFacet(values.map((item) => item.reportingCurrency), rows.length),
    maturity: buildFacet(values.map((item) => item.maturity), rows.length),
  };
}

export function creativeDecisionMatchesPortfolioFilters(
  row: CreativeDecisionProjection,
  request: CreativeDecisionPortfolioRequest,
): boolean {
  const values = deriveCreativeDecisionFilterValues(row);
  const contexts = row.monetizationContexts?.length
    ? row.monetizationContexts
    : [row.monetization];
  const has = (requested: readonly string[] | undefined, value: string): boolean => (
    !requested?.length || requested.includes(value)
  );
  const overlaps = (
    requested: readonly string[] | undefined,
    available: readonly string[],
    normalize: (value: string) => string = (value) => value,
  ): boolean => !requested?.length || requested.some((candidate) => (
    available.map(normalize).includes(normalize(candidate))
  ));
  const legacyCurrencies = request.currencies?.map((currency) => currency.toUpperCase());

  return has(request.providers, row.deployment.provider)
    && has(request.providerAccountIds, row.deployment.providerAccountId)
    && has(request.lifecycleStages, row.lifecycleStage)
    && has(request.dataStates, row.dataState)
    && has(request.decisions, row.recommendation.decision)
    && has(request.origins, row.deployment.origin)
    && overlaps(request.evidenceClasses, values.evidenceClass)
    && overlaps(request.campaignFamilies, values.campaignFamily)
    && overlaps(request.networks, values.network)
    && overlaps(request.funnels, values.funnel)
    && overlaps(request.geos, values.geo)
    && overlaps(request.productIds, values.product)
    && overlaps(request.planIds, values.plan)
    && overlaps(request.priceIds, values.price)
    && overlaps(request.originalCurrencies, values.originalCurrency, upper)
    && overlaps(request.reportingCurrencies, values.reportingCurrency, upper)
    && overlaps(request.maturityStates, values.maturity)
    && (!legacyCurrencies?.length
      || overlaps(legacyCurrencies, [...values.originalCurrency, ...values.reportingCurrency], upper))
    && contexts.some((context) => (
      has(request.purchaseModes, context.purchaseMode)
      && has(request.subscriptionEntries, context.subscriptionEntry)
    ));
}

function buildFacet(valuesByRow: readonly string[][], rowCount: number): CreativeDecisionFilterFacet {
  const presentCount = valuesByRow.filter((values) => values.length > 0).length;
  const values = clean(valuesByRow.flat()).sort((left, right) => left.localeCompare(right));
  if (rowCount === 0 || presentCount === 0) {
    return {
      state: 'unavailable',
      values,
      reasonCodes: ['dimension_not_available_in_projection'],
    };
  }
  if (presentCount < rowCount) {
    return {
      state: 'partial',
      values,
      reasonCodes: ['dimension_missing_for_some_deployments'],
    };
  }
  return { state: 'available', values, reasonCodes: [] };
}

function deriveMaturityState(row: CreativeDecisionProjection): CreativeDecisionMaturityState {
  if (row.maturity?.state) return row.maturity.state;
  if (row.dataState === 'too_young') return 'immature';
  if (row.lifecycleStage === 'mature' || row.lifecycleStage === 'scaling'
    || row.lifecycleStage === 'fatiguing' || row.lifecycleStage === 'refreshed'
    || row.lifecycleStage === 'archived') return 'mature';
  return 'unknown';
}

function moneyBindings(row: CreativeDecisionProjection): MoneyAmountBinding[] {
  return [
    row.money.selectedWindowSpend,
    row.money.currentDailySpend,
    row.money.currentPartialDaySpend,
    row.money.unchangedPaceSevenDayExposure,
    row.money.grossCollected,
    row.money.netCollected,
    row.money.settlementCash,
    row.money.realizedContributionBeforeAdSpend,
    row.money.realizedContributionAfterAdSpend,
    row.money.projectedContributionP10,
    row.money.projectedContributionP50,
    row.money.projectedContributionP90,
    ...(row.money.firstPartyOriginalCurrencyBreakdown ?? []).flatMap((bucket) => [
      bucket.grossCollected,
      bucket.netCollected,
      bucket.settlementCash,
      bucket.contribution,
    ]),
  ].filter((value): value is MoneyAmountBinding => Boolean(value));
}

function clean(values: readonly (string | undefined)[]): string[] {
  return [...new Set(values.map((value) => value?.trim()).filter((value): value is string => Boolean(value)))];
}

function upper(value: string): string {
  return value.toUpperCase();
}
